import React, { useState, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import { Order } from '../interfaces';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import SuccessNotification from './notifications/SuccessNotification';
import './PaymentPopup.css';
import './CardPaymentPopup.css';
import { useAuth } from '../AuthContext';

interface CardPaymentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    activeOrder: Order | undefined;
    onPaymentComplete?: () => void;
    onPartialPayment?: () => void;
    seatTotal?: number;
    reservationInfo?: { reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string };
}

const CardPaymentPopup: React.FC<CardPaymentPopupProps> = ({ isOpen, onClose, activeOrder, onPaymentComplete, onPartialPayment, seatTotal, reservationInfo }) => {
    const { hasPerm } = useAuth();
    const can = (key: string) => !hasPerm || hasPerm(key);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [inputValue, setInputValue] = useState<string>('');
    const [order, setOrder] = useState(activeOrder);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCardType, setSelectedCardType] = useState('visa');
    const [confirmationNumber, setConfirmationNumber] = useState('');
    const [showKeypad, setShowKeypad] = useState(false);
    const isFinalizeButtonDisabled = !confirmationNumber.trim() || !inputValue.trim();

    function generateGUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    const formatNumberWithCommas = (value: string) => {
        const cleanValue = value.replace(/,/g, '');
        const parts = cleanValue.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = parts[1] ? `.${parts[1]}` : '';
        return integerPart + decimalPart;
    };
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const handleButtonClick = (char: string) => {
        if (char === '.') {
            if (!inputValue.includes('.')) {
                setInputValue(inputValue + '.');
            }
        } else {
            setInputValue(formatNumberWithCommas(inputValue + char));
        }
    };
    const navigate = useNavigate();

    const handleFinalizeClick = async () => {
        if (!can('pos-app.payment.card')) return;
        const paymentAmount = parseFloat(inputValue.replace(/,/g, '') || "0");
        const change = parseFloat(getChange());
        const balance = parseFloat(getBalance());

        if (paymentAmount != 0) {
            const newPayment = {
                id: generateGUID(),
                amount: paymentAmount,
                type: "card",
                date: new Date(),
                change: change,
                balanceBefore: balance,
                confirmationNumber: confirmationNumber,
                cardType: selectedCardType,
                reservationId: reservationInfo?.reservationId || '',
                reservationNumber: reservationInfo?.reservationNumber || '',
                roomNumber: reservationInfo?.roomNumber || '',
                roomWithStatusId: reservationInfo?.roomWithStatusId || '',
                seatId: reservationInfo?.seatId || '',
                guestName: reservationInfo?.guestName || '',
            };

            if (order?.id) {
                const updatedOrder = {
                    ...order,
                    payments: [...(order?.payments || []), newPayment],
                };
                await saveOrder(updatedOrder);
            }
        }
    };

    const saveOrder = async (updatedOrder: Order) => {
        try {
            setIsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: updatedOrder.id,
                    propertyId: updatedOrder.propertyId,
                    products: updatedOrder.products,
                    customerEmail: updatedOrder.customerEmail,
                    customerName: updatedOrder.customerName,
                    // This is the request that closes the order. Without the customer link and
                    // the seats, nothing here identifies who the order was for, so it cannot be
                    // recorded against their history.
                    customerId: updatedOrder.customerId,
                    seats: updatedOrder.seats,
                    currency: updatedOrder.currency,
                    subtotal: updatedOrder.subtotal,
                    total: updatedOrder.total,
                    tax: updatedOrder.tax,
                    isTaxPercentage: updatedOrder.isTaxPercentage,
                    discount: updatedOrder.discount,
                    isOpen: updatedOrder.isOpen,
                    tableId: updatedOrder.tableId,
                    tableName: updatedOrder.tableName,
                    orderDiscountType: updatedOrder.orderDiscountType,
                    orderDiscountValue: updatedOrder.orderDiscountValue,
                    orderDiscountName: updatedOrder.orderDiscountName,
                    status: updatedOrder.status,
                    orderNumber: updatedOrder.orderNumber,
                    waiter: updatedOrder.waiter,
                    date: updatedOrder.date,
                    closedBy: updatedOrder.closedBy,
                    createdBy: updatedOrder.createdBy,
                    orderPaidOn: updatedOrder.orderPaidOn,
                    isVoided: updatedOrder.isVoided,
                    combinations: updatedOrder.combinations,
                    subtotalWithoutDiscounts: updatedOrder.subtotalWithoutDiscounts,
                    payments: updatedOrder.payments,
                    roomNumber: updatedOrder.roomNumber || activeOrder?.roomNumber,
                    prostayReservationNumber: updatedOrder.prostayReservationNumber || activeOrder?.prostayReservationNumber,
                    orderRoomAllocations: updatedOrder.orderRoomAllocations || activeOrder?.orderRoomAllocations || [],
                }),
                credentials: 'include',
            });
            if (response) {
                setInputValue('');
                setOrder(updatedOrder);
                if (updatedOrder.payments && updatedOrder.payments.length > 0) {
                    const totalPaid = updatedOrder.payments.reduce((sum: number, p: any) => sum + (Number(p.amount) || 0) - (Number(p.change) || 0), 0);
                    const orderTotal = updatedOrder.total || 0;
                    const isFullyPaid = totalPaid >= orderTotal;

                    if (isFullyPaid) {
                        setHeaderTitle('Success!');
                        setContent('Order was closed successfully.');
                        setShowSuccess(true);
                        setTimeout(() => {
                            setShowSuccess(false);
                            if (onPaymentComplete) onPaymentComplete();
                            navigate('/orders');
                        }, 3000);
                    } else {
                        setHeaderTitle('Payment Recorded');
                        setContent(`Payment of ${formatPrice(Number(inputValue.replace(/,/g, '')))} recorded. Remaining: ${formatPrice(orderTotal - totalPaid)}`);
                        setShowSuccess(true);
                        setTimeout(() => {
                            setShowSuccess(false);
                            if (onPartialPayment) onPartialPayment();
                            else onClose();
                        }, 2000);
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (activeOrder) {
            setOrder(activeOrder);
        }
    }, [activeOrder]);

    // Card charges are almost always for the full balance, so prefill the amount
    // on open — the common path is select brand + reference, no typing required.
    useEffect(() => {
        if (isOpen) {
            const bal = getBalance();
            setInputValue(bal && bal !== '0.00' ? formatNumberWithCommas(bal) : '');
            setShowKeypad(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);


    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const styles = StyleSheet.create({
        drawer: {
            top: 0,
            bottom: 0,
            right: 0,
            width: windowSize.width > 600 ? 600 : windowSize.width * 0.9,
            height: windowSize.height,
            backgroundColor: 'white',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: '#D0D5DD',
            elevation: 5,
            zIndex: 1002,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
        },
        openDrawer: {
            right: 0,
        },
        closedDrawer: {
            right: -(windowSize.width > 900 ? windowSize.width * 0.47 : windowSize.width * 0.9),
        },
        orderProductsContainer: {
            maxHeight: windowSize.height - 460,
        },
        spinnerOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(256, 256, 256, 0.8)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100,
        },
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const formatDateTime = (date: Date | string) => {
        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        const options: Intl.DateTimeFormatOptions = {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        };

        const formatter = new Intl.DateTimeFormat("en-GB", options);
        return formatter.format(date).replace(',', '');
    };

    const handleEraseClick = () => {
        if (inputValue != '') {
            setInputValue((prev) => prev.slice(0, -1));
        }
    };

    const getBalance = () => {
        if (!order) return "0.00";

        // For split bill (seatTotal set), the balance is just the seat amount — don't subtract other seats' payments
        if (seatTotal !== undefined) {
            return isNaN(seatTotal) ? "0.00" : seatTotal.toFixed(2);
        }

        const total = order.total || 0;
        const payments = order?.payments || [];
        const totalPayments = payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0) - (Number(payment.change) || 0), 0);
        const balance = (Number(total) || 0) - totalPayments;

        return isNaN(balance) ? "0.00" : balance.toFixed(2);
    }

    const getChange = () => {
        const balance = parseFloat(getBalance());
        const inputValueNumber = parseFloat(inputValue || "0");

        if (inputValueNumber > balance) {
            return (inputValueNumber - balance).toFixed(2);
        }

        return "0.00";
    };
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (!isOpen) return;
            if (!showKeypad) return;
            const { key } = event;

            if (!isNaN(Number(key))) {
                setInputValue((prev) => formatNumberWithCommas(prev + key));
            } else if (key === '.' && !inputValue.includes('.')) {
                setInputValue((prev) => prev + '.');
            } else if (key === 'Backspace') {
                setInputValue((prev) => prev.slice(0, -1));
            } else if (key === 'Enter') {
                handleFinalizeClick();
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, inputValue, showKeypad]); // Ignore missing dependencies warning

    const amountDue = (() => {
        const b = parseFloat(getBalance());
        return isNaN(b) ? 0 : Math.max(0, b);
    })();
    const charged = parseFloat(inputValue.replace(/,/g, '') || '0') || 0;
    const changeDue = charged > amountDue ? charged - amountDue : 0;

    return (
      <>
        <div className={`paypop-scrim${isOpen ? ' is-open' : ''}`} onClick={onClose} />
        <div className={`paypop ps-scope${isOpen ? ' is-open' : ''}`} style={{ ...styles.drawer, ...(isOpen ? styles.openDrawer : styles.closedDrawer) }}>
            <div className='cardpay'>
                <header className='cardpay__head'>
                    <div className='cardpay__head-id'>
                        <span className='cardpay__eyebrow'>Card payment</span>
                        <span className='cardpay__table'>{order?.tableName}{activeOrder?.customerName ? ` · ${activeOrder?.customerName}${activeOrder?.roomNumber ? ` · ${activeOrder?.roomNumber}` : ''}` : ''}</span>
                    </div>
                    <button className='cardpay__close' onClick={onClose} aria-label='Close'>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </header>

                <div className='cardpay__body'>
                    <div className='cardpay__hero'>
                        <span className='cardpay__hero-label'>Amount due</span>
                        <span className='cardpay__hero-amt'>{formatPrice(amountDue)}</span>
                        {charged > 0 && (
                            <span className='cardpay__hero-sub'>
                                Charging {formatPrice(charged)}{changeDue > 0 ? ` · Change ${formatPrice(changeDue)}` : ''}
                            </span>
                        )}
                    </div>

                    <div className='cardpay__section'>
                        <span className='cardpay__section-label'>Card type</span>
                        <select
                            value={selectedCardType}
                            onChange={(e) => setSelectedCardType(e.target.value)}
                            style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #D0D5DD', backgroundColor: '#fff', fontSize: 14 }}
                        >
                            <option value='visa'>Visa</option>
                            <option value='mastercard'>Mastercard</option>
                            <option value='maestro'>Maestro</option>
                            <option value='amex'>American Express</option>
                            <option value='jcb'>JCB</option>
                        </select>
                    </div>

                    <div className='cardpay__section'>
                        <div className='cardpay__section-head'>
                            <span className='cardpay__section-label'>Amount charged</span>
                            <button
                                type='button'
                                className='cardpay__ghost'
                                onClick={() => { setInputValue(formatNumberWithCommas(amountDue.toFixed(2))); setShowKeypad(false); }}
                            >
                                Full balance
                            </button>
                        </div>
                        <button
                            type='button'
                            className={`cardpay__amount${showKeypad ? ' is-open' : ''}`}
                            onClick={() => setShowKeypad((v) => !v)}
                        >
                            <span className='cardpay__amount-val'>{getCurrencySymbol()}{inputValue || '0'}</span>
                            <span className='cardpay__amount-edit'>{showKeypad ? 'Done' : 'Edit'}</span>
                        </button>
                        {showKeypad && (
                            <div className='cardpay__keypad'>
                                <div className='cardpay__keys'>
                                    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map((char) => (
                                        <button key={char} type='button' className='cardpay__key' onClick={() => handleButtonClick(char)}>{char}</button>
                                    ))}
                                    <button type='button' className='cardpay__key cardpay__key--erase' onClick={handleEraseClick} aria-label='Backspace'>
                                        <svg width="22" height="20" viewBox="0 0 36 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M26.3333 9.49984L16.3333 19.4998M16.3333 9.49984L26.3333 19.4998M2.53333 16.0998L9.73333 25.6998C10.32 26.4821 10.6133 26.8732 10.9851 27.1553C11.3143 27.4051 11.6872 27.5915 12.0846 27.705C12.5333 27.8332 13.0222 27.8332 14 27.8332H26.6667C29.4669 27.8332 30.8671 27.8332 31.9366 27.2882C32.8774 26.8088 33.6423 26.0439 34.1217 25.1031C34.6667 24.0336 34.6667 22.6334 34.6667 19.8332V9.1665C34.6667 6.36624 34.6667 4.96611 34.1217 3.89655C33.6423 2.95574 32.8774 2.19084 31.9366 1.71147C30.8671 1.1665 29.4669 1.1665 26.6667 1.1665H14C13.0222 1.1665 12.5333 1.1665 12.0846 1.29466C11.6872 1.40816 11.3143 1.59458 10.9851 1.84442C10.6133 2.12651 10.32 2.51762 9.73333 3.29984L2.53333 12.8998C2.10301 13.4736 1.88785 13.7605 1.80491 14.0756C1.7317 14.3537 1.7317 14.646 1.80491 14.9241C1.88785 15.2392 2.10301 15.5261 2.53333 16.0998Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='cardpay__section'>
                        <span className='cardpay__section-label'>Confirmation number</span>
                        <input
                            className='cardpay__input'
                            type="text"
                            value={confirmationNumber}
                            onChange={(e) => setConfirmationNumber(e.target.value)}
                            placeholder="Enter reference from the card terminal"
                        />
                        <p className='cardpay__hint'>
                            Charge the guest on the card terminal first, then enter the payment reference / confirmation number to finalize the order.
                        </p>
                    </div>
                </div>

                <footer className='cardpay__foot'>
                    <button
                        type='button'
                        className='cardpay__confirm'
                        disabled={isFinalizeButtonDisabled}
                        onClick={handleFinalizeClick}
                    >
                        {!inputValue.trim()
                            ? 'Enter amount charged'
                            : !confirmationNumber.trim()
                                ? 'Enter confirmation number'
                                : <>Finalize · {formatPrice(charged)}</>}
                    </button>
                </footer>
            </div>

            {isLoading && (
                <div>
                    <div style={styles.spinnerOverlay}></div>
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1101,
                        }}
                    >
                        <CircularProgress
                            style={{
                                zIndex: 1102,
                            }}
                        />
                    </div>
                </div>
            )}
            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
        </div>
      </>
    );
};

export default CardPaymentPopup;

